import { useCallback, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { usePermissions } from '@/modules/common/hooks/use-permissions';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';
import { AdjustBalanceModal } from '@/modules/time-off/components/balances/adjust-balance-modal';
import { MemberTimeOffsView } from '@/modules/time-off/components/member/member-time-offs-view';
import { TimeOffBalance } from '@/modules/time-off/types/time-off-balance';
import { TimeOffRequest } from '@/modules/time-off/types/time-off-request';
import { AuthenticatedRoutes } from '@/router/routes';

import { TeamMember } from '../../../types';

interface TeamMemberTimeOffTabProps {
  featureData?: TeamMember;
}

/**
 * Team-member details "Time-off" tab. Shows the member's balances + request
 * history (mocked). The balance-edit pencil + adjust modal are gated behind the
 * manage-balance permission; without it the tab is read-only.
 */
export const TeamMemberTimeOffTab = ({ featureData }: TeamMemberTimeOffTabProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageBalance = hasPermission(OpsiumPolicy.TimeOffBalancesEditOthers);

  const memberId = featureData?.id ? String(featureData.id) : undefined;

  const [editingBalance, setEditingBalance] = useState<TimeOffBalance | null>(null);

  const buildRowHref = useCallback(
    (request: TimeOffRequest) => {
      if (!memberId) return undefined;
      return AuthenticatedRoutes.TeamMemberTimeOffRequestDetails.replace(
        ':memberId',
        memberId
      ).replace(':requestId', String(request.id));
    },
    [memberId]
  );

  const handleRowClick = useCallback(
    (request: TimeOffRequest) => {
      const href = buildRowHref(request);
      if (href) navigate(href);
    },
    [navigate, buildRowHref]
  );

  const handleEditRequest = useCallback(
    (request: TimeOffRequest) => {
      if (!memberId) return;
      navigate(
        AuthenticatedRoutes.TeamMemberTimeOffRequestDetails.replace(':memberId', memberId).replace(
          ':requestId',
          String(request.id)
        ),
        { state: { startInEditMode: true } }
      );
    },
    [navigate, memberId]
  );

  if (!memberId) return null;

  return (
    <>
      <div className="h-full min-h-0">
        <MemberTimeOffsView
          memberId={memberId}
          onRowClick={handleRowClick}
          onEditRequest={handleEditRequest}
          getRowHref={buildRowHref}
          onEditBalance={canManageBalance ? setEditingBalance : undefined}
        />
      </div>

      <AdjustBalanceModal
        memberId={memberId}
        balance={editingBalance}
        open={editingBalance !== null}
        onClose={() => setEditingBalance(null)}
      />
    </>
  );
};
